import type { StringNode, TemplateNode } from "../../composables/Api/Type/AutomationTemplate/automationTemplate.interface";

/** Characters a variable counts for against a maximum length: its value is not known while typing. */
export const VARIABLE_LENGTH = 20;

export function textNode(text: string): StringNode {
  return { "#type": "AutomationTemplate.Node.String", text };
}

/** Joins neighbouring text nodes; an empty list becomes one empty text node. */
export function normalizeNodes(nodes: TemplateNode[]): TemplateNode[] {
  const result: TemplateNode[] = [];
  for (const node of nodes) {
    const last = result[result.length - 1];
    if (node["#type"] === "AutomationTemplate.Node.String" && last?.["#type"] === "AutomationTemplate.Node.String") {
      result[result.length - 1] = textNode(last.text + node.text);
      continue;
    }
    result.push({ ...node });
  }
  return result.length > 0 ? result : [textNode("")];
}

/** Whether two node lists hold the same text, however their text is split over nodes. */
export function nodesEqual(left: TemplateNode[], right: TemplateNode[]): boolean {
  const a = normalizeNodes(left);
  const b = normalizeNodes(right);
  return a.length === b.length && a.every((node, index) => {
    const other = b[index];
    if (node["#type"] === "AutomationTemplate.Node.String") {
      return other?.["#type"] === "AutomationTemplate.Node.String" && other.text === node.text;
    }
    return other?.["#type"] === "AutomationTemplate.Node.Variable" && other.identifier === node.identifier;
  });
}

export function nodesLength(nodes: TemplateNode[]): number {
  return nodes.reduce((sum, node) => sum + (node["#type"] === "AutomationTemplate.Node.String" ? node.text.length : VARIABLE_LENGTH), 0);
}

export function nodesAreEmpty(nodes: TemplateNode[] | null | undefined): boolean {
  return (nodes ?? []).every(node => node["#type"] === "AutomationTemplate.Node.String" && node.text.trim() === "");
}

/** Variable identifiers in order of appearance, with duplicates. */
export function nodeVariables(nodes: TemplateNode[]): string[] {
  return nodes.flatMap(node => (node["#type"] === "AutomationTemplate.Node.Variable" ? [node.identifier] : []));
}
